import { Entity, IEntity } from '@lycoris-nubila/ngx-lycoris';
import isArray from 'lodash-es/isArray';
import isNaN from 'lodash-es/isNaN';
import isPlainObject from 'lodash-es/isPlainObject';
import isString from 'lodash-es/isString';
import pickBy from 'lodash-es/pickBy';
import transform from 'lodash-es/transform';
import values from 'lodash-es/values';
import { DateTime } from 'luxon';
import { Observable, of } from 'rxjs';

import { Document, IDocument } from 'document/document';
import {
	IModuleQuestion,
	ModuleQuestion,
} from 'module/page/question/module-question';
import { UserModuleAnswerType } from 'user/model/module/answer/user-module-answer.type';

import { UserModuleAnswerTask } from './user-module-answer-task';

export interface AnswerValue {
	answer: any;
	title?: string;
	duplicates: any[];
	siblings: AnswerValue[];
	sourceEntityId?: string;
}

export interface IUserModuleAnswer extends IEntity {
	readonly value: string;
	readonly userId: string;
	readonly pageId: string;
	readonly moduleId: string;
	readonly contactId: string;
	readonly articleId: string;
	readonly documentId: string;
	readonly questionId: string;
	readonly orgModuleId: string;
	readonly reportTaskId: string;
	readonly questionName: string;
	readonly reportJobOfferId: string;
	readonly type: UserModuleAnswerType;
	readonly task: UserModuleAnswerTask;
	readonly document$: Observable<IDocument>;
	readonly question$: Observable<IModuleQuestion>;
	readonly objectValue: AnswerValue;
	readonly stringValues: ReadonlyArray<string>;
}

export class UserModuleAnswer extends Entity implements IUserModuleAnswer {
	private _value: string;
	private _userId: string;
	private _pageId: string;
	private _moduleId: string;
	private _contactId: string;
	private _articleId: string;
	private _documentId: string;
	private _questionId: string;
	private _orgModuleId: string;
	private _questionName: string;
	private _reportTaskId: string;
	private _reportJobOfferId: string;
	private _type: UserModuleAnswerType;
	private _task: UserModuleAnswerTask;
	private _document$: Observable<Document>;
	private _question$: Observable<ModuleQuestion>;
	private _objectValue: AnswerValue;
	private _stringValues: string[];

	constructor(
		id: string,
		value: string,
		userId: string,
		pageId: string,
		moduleId: string,
		contactId: string,
		articleId: string,
		documentId: string,
		questionId: string,
		orgModuleId: string,
		questionName: string,
		reportTaskId: string,
		updateTimestamp: number,
		reportJobOfferId: string,
		type: UserModuleAnswerType,
		task: UserModuleAnswerTask,
		document$: Observable<Document> = of(null),
		question$: Observable<ModuleQuestion> = of(null)
	) {
		super(id, null, updateTimestamp);
		this._value = value;
		this._userId = userId;
		this._pageId = pageId;
		this._moduleId = moduleId;
		this._contactId = contactId;
		this._articleId = articleId;
		this._documentId = documentId;
		this._questionId = questionId;
		this._orgModuleId = orgModuleId;
		this._reportTaskId = reportTaskId;
		this._questionName = questionName;
		this._reportJobOfferId = reportJobOfferId;
		this._type = type;
		this._task = task;
		this._document$ = document$;
		this._question$ = question$;
		this._loadValue();
	}

	public get value(): string {
		return this._value;
	}

	public get userId(): string {
		return this._userId;
	}

	public get pageId(): string {
		return this._pageId;
	}

	public get moduleId(): string {
		return this._moduleId;
	}

	public get contactId(): string {
		return this._contactId;
	}

	public get articleId(): string {
		return this._articleId;
	}

	public get documentId(): string {
		return this._documentId;
	}

	public get questionId(): string {
		return this._questionId;
	}

	public get orgModuleId(): string {
		return this._orgModuleId;
	}

	public get reportTaskId(): string {
		return this._reportTaskId;
	}

	public get questionName(): string {
		return this._questionName;
	}

	public get reportJobOfferId(): string {
		return this._reportJobOfferId;
	}

	public get type(): UserModuleAnswerType {
		return this._type;
	}

	public get task(): UserModuleAnswerTask {
		return this._task;
	}

	public get document$(): Observable<Document> {
		return this._document$;
	}

	public get question$(): Observable<ModuleQuestion> {
		return this._question$;
	}

	public get objectValue(): AnswerValue {
		return this._objectValue;
	}

	public get stringValues(): string[] {
		return this._stringValues;
	}

	private _loadValue() {
		const val = JSON.parse(this._value);
		if (isPlainObject(val)) {
			this._objectValue = this._transformObject(val);
		} else {
			this._objectValue = val;
		}

		this._stringValues = [
			...values(pickBy(this._objectValue.answer, (v, k) => k !== 'entityId')),
			...(this.objectValue.duplicates || [])
				.map((d) => values(pickBy(d, (v, k) => k !== 'entityId')))
				.reduce((a, b) => [...a, ...b], []),
		].filter((v) => !!v);
	}

	private _transformObject(object: any): AnswerValue {
		return transform(
			object,
			(result, value, key) => {
				result[key] = this._parseValue(value);
			},
			{} as AnswerValue
		);
	}

	private _parseValue(value: any): any {
		if (isString(value)) {
			return !isNaN(Number(value)) || !DateTime.fromISO(value).isValid
				? value
				: DateTime.fromISO(value).toJSDate();
		} else if (isPlainObject(value)) {
			return this._transformObject(value);
		} else if (isArray(value)) {
			return value.map((val) => this._parseValue(val));
		} else {
			return value;
		}
	}
}
